import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';
import { useLoader } from '../context/loaders/useLoader';
import { useToast } from '../context/toasts/useToast';
import type { CardLinksResponse } from '../types/kanban';

export function useCardLinks(cardId: number) {
  const [links, setLinks] = useState<CardLinksResponse>({
    child_cards: [],
    parent_cards: [],
  });
  const [error, setError] = useState<Error | null>(null);

  const { getCardLinks, createCardLink, deleteCardLink } = useApi();
  const { withLoader } = useLoader();
  const { addNotification } = useToast();

  const fetchLinks = useCallback(async () => {
    try {
      setError(null);
      const data = await getCardLinks(cardId);
      setLinks(data);
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load card links';
      setError(err instanceof Error ? err : new Error(errorMessage));
      addNotification({
        type: 'error',
        title: 'Error Loading Links',
        message: errorMessage,
        duration: 5000,
      });
      return { child_cards: [], parent_cards: [] };
    }
  }, [getCardLinks, cardId, addNotification]);

  const linkCard = useCallback(
    async (childCardId: number) => {
      try {
        setError(null);
        await withLoader(() => createCardLink(cardId, childCardId), 'Linking card...');
        addNotification({
          type: 'success',
          title: 'Card Linked',
          message: 'Card linked successfully.',
          duration: 3000,
        });
        await fetchLinks();
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to link card';
        setError(err instanceof Error ? err : new Error(errorMessage));
        addNotification({
          type: 'error',
          title: 'Error Linking Card',
          message: errorMessage,
          duration: 5000,
        });
        return false;
      }
    },
    [cardId, createCardLink, withLoader, addNotification, fetchLinks]
  );

  const unlinkCard = useCallback(
    async (linkId: number) => {
      try {
        setError(null);
        await withLoader(() => deleteCardLink(linkId), 'Unlinking card...');
        addNotification({
          type: 'success',
          title: 'Card Unlinked',
          message: 'Card unlinked successfully.',
          duration: 3000,
        });
        await fetchLinks();
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to unlink card';
        setError(err instanceof Error ? err : new Error(errorMessage));
        addNotification({
          type: 'error',
          title: 'Error Unlinking Card',
          message: errorMessage,
          duration: 5000,
        });
        return false;
      }
    },
    [deleteCardLink, withLoader, addNotification, fetchLinks]
  );

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  return {
    links,
    error,
    loading: false,
    fetchLinks,
    linkCard,
    unlinkCard,
  };
}
